import List from 'components/Common/List'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import Divider from 'LuxKit/Divider'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import React, { useState } from 'react'
import styled from 'styled-components'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LineWrenchIcon from 'LuxKit/Icons/line/LineWrenchIcon'
import LineBackpackIcon from 'LuxKit/Icons/line/LineBackpackIcon'
import LineKeySkeletonAltIcon from 'LuxKit/Icons/line/LineKeySkeletonAltIcon'
import LineMedicalSquareIcon from 'LuxKit/Icons/line/LineMedicalSquareIcon'
import LineFileCheckAltIcon from 'LuxKit/Icons/line/LineFileCheckAltIcon'
import LinePumpIcon from 'LuxKit/Icons/line/LinePumpIcon'

const TabContent = styled(Group)`
  min-height: ${rem(275)};
  padding-top: ${rem(16)};
`

const StyledList = styled(List)`
  ol {
    margin: 0;
  }
`

const StyledBodyTextBlock = styled(BodyTextBlock)`
  white-space: pre-line;
`

interface Props {
  carHireSupplier: string
  insuranceQuoteInfo: App.CarHIreOfferInsuranceQuoteInformation
  hasEnhancedProtection: boolean
}

function CarHireInsuranceEnhancedProtectionInfo(props: Props) {
  const { carHireSupplier, insuranceQuoteInfo: insuranceInfo, hasEnhancedProtection } = props
  const tabOptions = ['What\'s covered', 'Things to know']
  const [activeTab, setActiveTab] = useState(tabOptions[0])

  const coveredItemsAUAndRestOfTheWorld = [
    {
      title: 'Third Party Protection',
      text: 'Included as part of your rental agreement. If you or any authorised driver are involved in an accident, Third Party injury and property damage is already covered.',
      icon: <LineShieldCheckIcon />,
    },
    {
      title: 'Accidents, theft & damage cover',
      text: "Our policy includes coverage for theft, as well as damage to the rental car's windscreen, glass, undercarraige, roof and tyres. Damage relating to road traffic or weather-related accidents, collision with animals, fire or explosion are also covered. If your car needs to be towed as the result of an accident, you're covered too.",
      icon: <LineWrenchIcon />,
    },
    {
      title: 'Personal possessions in car',
      text: "We safeguard your personal items against damage or theft in the rental car's locked areas.",
      icon: <LineBackpackIcon />,
    },
    {
      title: 'Lost keys & personal possessions',
      text: 'We cover lost keys and stolen personal items from your car.',
      icon: <LineKeySkeletonAltIcon />,
    },
    {
      title: 'Medical & accidental death coverage',
      text: 'Our policy includes coverage for car-related accidents or death.',
      icon: <LineMedicalSquareIcon />,
    },
    {
      title: 'Pre-booked travel arrangements',
      text: "We've got you covered if your rental car experiences issues like inoperability, theft, or accidents that disrupt your pre-booked travel arrangements.",
      icon: <LineFileCheckAltIcon />,
    },
  ]

  const coveredItemsUSDestination = [
    {
      title: 'Third Party Protection',
      text: 'Included as part of your rental agreement. If you or any authorised driver are involved in an accident, Third Party injury and property damage is already covered.',
      icon: <LineShieldCheckIcon />,
    },
    {
      title: 'Accidents, theft & damage cover',
      text: `Accidental Damage or Theft, including damage to a third party not covered by the rental company relating to damage or theft of the vehicle.
      \nThis could include damage to your rental car's windscreen, glass, undercarriage, roof and tyres.
      \nIf your car needs to be towed as a result of an accident you are covered too.`,
      icon: <LineWrenchIcon />,
    },
    {
      title: 'Lost keys & personal possessions in car',
      text: 'We cover lost keys and stolen personal items from your car.',
      icon: <LineBackpackIcon />,
    },
    {
      title: 'Misfuelling cover',
      text: 'We cover against the costs incurred as a result of You putting the wrong type of fuel into a rental vehicle.',
      icon: <LinePumpIcon />,
    },
    {
      title: 'Administrative fees',
      text: 'We cover against administration charges applied by a rental company which cannot be recovered following a claim which is covered by this insurance.',
      icon: <LineShieldCheckIcon />,
    },
    {
      title: 'Unused rental days',
      text: 'You are eligible for a refund if the car rental agreement is cancelled or cut short due to a medical condition or accident',
      icon: <LineFileCheckAltIcon />,
    },
  ]

  return (
    <>
      <TabButtonsGroup>
        {tabOptions.map((option) => (
          <TabButton
            isActive={option === activeTab}
            key={option}
            size="medium"
            onClick={() => setActiveTab(option)}
          >
            {option}
          </TabButton>
        ))}
      </TabButtonsGroup>
      {insuranceInfo &&
        <VerticalSpacer gap={20}>
          <TabContent direction="vertical" gap={16}>
            {activeTab === 'What\'s covered' &&
              <>
                {!hasEnhancedProtection && <>
                  <BodyTextBlock startIcon={(<LineCheckCircleIcon />)} variant="medium" weight="bold">100% Refundable Excess</BodyTextBlock>
                  <BodyTextBlock variant="medium">The policy will reimburse the excess you need to pay when there’s an incident with your rental vehicle. In the case of damage to your rental car, your excess (<b>up to <FormatCurrency currency={insuranceInfo.planPriceCurrency} value={insuranceInfo.policyLimit} /></b>) will be fully refunded by AWN insurance.</BodyTextBlock>
                  <Divider kind="primary" />
                </>}

                {(hasEnhancedProtection ? coveredItemsUSDestination : coveredItemsAUAndRestOfTheWorld).map((item, index, arr) => <>
                  <BodyTextBlock startIcon={item.icon} variant="medium" weight="bold">{item.title}</BodyTextBlock>
                  <StyledBodyTextBlock style={{ whiteSpace: 'pre-line' }} variant="medium">{item.text}</StyledBodyTextBlock>
                  {index + 1 !== arr.length &&
                    <Divider kind="primary" />}
                </>)}
              </>}

            {activeTab === 'Things to know' && insuranceInfo?.policyLimit > 0 &&
              <>
                <List spaced>
                  <li>Make sure you have a credit or debit card in the main driver's name and enough credit to hold the refundable deposit at the rental desk.</li>
                  <li>{carHireSupplier} may ask you to buy insurance at the rental desk. If you add Enhanced Protection you can refuse insurance at the rental desk with confidence.</li>
                  <li>Enhanced Protection covers up to a maximum total policy limit of <FormatCurrency currency={insuranceInfo.planPriceCurrency} value={insuranceInfo.policyLimit} /> if there is damage or theft of the car during the rental period. When you book a car and add Enhanced Protection, Limited cover is also included.</li>
                  <li><b>AWN Insurance</b> will refund you if you need to cancel Enhanced Protection anytime prior to pick up</li>
                </List>
                <MessageBanner
                  kind="warning"
                  heading="If there's an accident, here's what to expect"
                  icon={<LineExclamationTriangleIcon />}
                >
                  <StyledList type="unstyled">
                    <ol>1. <b>{carHireSupplier} </b> will charge you the cost of damage, up to <FormatCurrency currency={insuranceInfo.planPriceCurrency} value={insuranceInfo.policyLimit} /></ol>
                    <ol>2. You can claim the amount you have been charged from <b>AWN Insurance</b></ol>
                    <ol>3. Once the claim is approved, <b>AWN Insurance</b> will refund you the amount you paid</ol>
                  </StyledList>
                </MessageBanner>
              </>}
          </TabContent>
          <TextLink href={insuranceInfo.termsAndConditionsUrl} target="_blank">Terms & Conditions</TextLink>
        </VerticalSpacer>}
    </>
  )
}

export default CarHireInsuranceEnhancedProtectionInfo
